import React, { useContext, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { useLocation } from 'react-router-dom';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import FolderSharedOutlinedIcon from '@mui/icons-material/FolderSharedOutlined';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import SettingsIcon from '@mui/icons-material/Settings';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';

import {
  AppBar,
  Avatar,
  Backdrop,
  Box,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
} from '@mui/material';

import { useNavigate } from 'react-router-dom';
import AccountCircle from '../assets/Account circle.svg';
import { sideBarWidth, spHeaderHeight } from '../../constants/location';
import { gray100, gray40, white, white_overlay30 } from '../../constants/colors';
import CustomSnackbar from './CustomSnackbar';
import { titleHeader } from '../../constants/basic';
import { signOut } from 'firebase/auth';
import { auth } from '../../api/firebase/config';
import { AuthContext } from '../../contexts/auth';
import { GroupContext } from '../../contexts/group';
import { userRole } from '../../constants/enum';

type Props = {
  pcIsNone?: boolean;
};

const SideBar = React.forwardRef((props: Props, _ref: React.Ref<unknown>): React.ReactElement => {
  const { pcIsNone } = props;
  const { user } = useContext(AuthContext);
  const { groups } = useContext(GroupContext);

  const styles = {
    button: {
      '&.Mui-disabled': {
        '.MuiListItemText-root': {
          '.MuiTypography-root': {
            fontWeight: '700',
          },
        },
        opacity: 1,
      },
      '&:hover': {
        backgroundColor: white_overlay30,
        borderRadius: '8px',
      },
      columnGap: '12px',
      height: '56px',
      borderRadius: '8px',
      padding: '16px 24px 16px 16px',
      '@media screen and (max-width:960px)': {
        padding: '16px',
      },
    },
    buttonSelected: {
      backgroundColor: white_overlay30,
      bordeRadius: '8px',
    },
    buttonText: {
      '.MuiTypography-root': {
        fontSize: '16px',
      },
      color: '#FFF',
    },
    hamburgerMenu: {
      padding: '12px',
      color: '#fff',
      marginLeft: 'auto',
    },
    header: {
      bgcolor: gray100,
      width: '100%',
    },
    headerTitle: {
      flexGrow: 1,
      display: 'flex',
      alignItems: 'center',
      // gap: '8px',
      minHeight: { xs: spHeaderHeight },
      padding: '0 12px',
    },
    headerWrapper: {
      flexGrow: 1,
      height: spHeaderHeight,
      position: 'sticky',
      top: 0,
    },
    icon: {
      color: '#FFF',
      width: '24px',
    },
    itemIcon: {
      minWidth: '24px',
    },
    link: {
      textDecoration: 'none',
    },
    list: {
      padding: 0,
      width: '224px',
      margin: '0 auto',
    },
    listItem: {
      padding: 0,
    },
    logoPc: {
      cursor: 'pointer',
      width: '80px',
    },
    logoSp: {
      cursor: 'pointer',
      width: '80px',
    },
    bottomButtons: {
      '@media screen and (max-width:960px)': {
        mt: 'auto',
      },
      width: '224px',
      margin: '0 auto',
    },
    sideBarWrapper: {
      '@media screen and (max-width:960px)': {
        height: '100vh',
        ml: 'auto',
        overflow: 'unset',
        position: 'fixed',
        right: 0,
        zIndex: 10,
        top: 0,
      },
      '@media screen and (min-width: 960px)': {
        display: pcIsNone ? 'none' : 'flex',
      },
      backgroundColor: gray100,
      height: '100vh',
      left: 0,
      position: 'sticky',
      overflowY: 'auto',
      top: 0,
      width: sideBarWidth,
      flexDirection: 'column',
      justifyContent: 'space-between',
    },
    navigationDrawer: {
      '@media screen and (max-width:960px)': {
        height: 'auto',
        mt: '16px',
        mb: '16px',
      },
      height: 'calc(100vh - 280px)',
      verflowY: 'auto',
      overflowX: 'hidden',
      webkitOverflowScrolling: 'touch',
    },
    topTitleWrapper: {
      padding: '24px 0px 24px 16px',
      textAlign: 'left',
    },
    title: {
      fontSize: '12px',
      color: 'rgba(255, 255, 255, 0.87)',
    },
    companyInfo: {
      display: 'flex',
      alignItems: 'center',
    },
    avatar: {
      width: 40,
      height: 40,
      backgroundColor: gray40,
      borderRadius: '50%',
    },
    companyName: {
      marginLeft: '8px',
    },
    account: {
      '@media screen and (max-width:960px)': {
        padding: '16px',
      },
      color: '#FFF',
      padding: '24px 16px 24px 16px',
    },
  };

  const sideBarText = {
    aiocr: 'AI文字読取',
    staff: '担当者一覧',
    logout: 'ログアウト',
  };

  const isMobile: boolean = useMediaQuery({ query: '(max-width: 960px)' });
  const [menuOpen, setMenuOpen] = React.useState<boolean>(false);

  const aiocrLink = '/ai-ocr';
  const staffLink = '/staff';

  const buttonList = [
    {
      icon: <HomeOutlinedIcon sx={styles.icon} />,
      link: aiocrLink,
      text: sideBarText.aiocr,
    },
    // 所属グループごとのAI OCR画面
    ...groups.map(group => ({
      icon: <FolderSharedOutlinedIcon sx={styles.icon} />,
      link: `/groups/${group.groupId}/ai-ocr`,
      text: group.name,
    })),
    {
      icon: <SupervisorAccountIcon sx={styles.icon} />,
      link: staffLink,
      text: sideBarText.staff,
    },
    // グループ管理（管理者のみ）
    ...(user?.role === userRole.admin
      ? [
          {
            icon: <SettingsIcon sx={styles.icon} />,
            link: '/groups/manage',
            text: 'グループ管理',
          },
        ]
      : []),
  ];

  const bottomButtonList = [
    {
      icon: <LogoutIcon sx={styles.icon} />,
      link: '/',
      text: sideBarText.logout,
    },
  ];

  const navigate = useNavigate();
  const location = useLocation();
  const [errorText, setErrorText] = useState<string>('');
  const [open, setOpen] = useState<boolean>(false);

  const logoutButton = async (): Promise<void> => {
    try {
      await signOut(auth);
      await navigate('/');
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      setOpen(true);
      setErrorText('ログアウトできませんでした');
    }
  };

  return (
    <>
      {/* SPヘッダー */}
      {isMobile && (
        <Box sx={{ ...styles.headerWrapper, zIndex: menuOpen ? 5 : 10 }}>
          <AppBar position="fixed" sx={styles.header} elevation={0}>
            <Toolbar sx={styles.headerTitle}>
              <Typography
                sx={{
                  Color: white,
                  fontSize: '24px',
                  fontWeight: '700',
                  ml: '11.12px',
                }}
              >
                {titleHeader}
              </Typography>
              <IconButton
                edge="end"
                sx={styles.hamburgerMenu}
                onClick={(): void => setMenuOpen(!menuOpen)}
              >
                {!menuOpen ? <MenuIcon /> : <CloseOutlinedIcon />}
              </IconButton>
            </Toolbar>
          </AppBar>
        </Box>
      )}

      <Backdrop
        sx={{ color: '#fff', zIndex: 9 }}
        open={menuOpen}
        onClick={(): void => setMenuOpen(false)}
      />

      {/* サイドバー ハンバーガーメニュー */}
      {(!isMobile || (isMobile && menuOpen)) && (
        <>
          <Box sx={styles.sideBarWrapper}>
            <Box sx={styles.topTitleWrapper}>
              <Typography
                sx={{
                  color: white,
                  fontSize: '32px',
                  fontWeight: '700',
                  whiteSpace: 'nowrap',
                }}
              >
                {titleHeader}
              </Typography>
            </Box>

            <Box
              sx={{
                ...styles.navigationDrawer,
              }}
            >
              <List sx={styles.list}>
                {buttonList.map(button => {
                  const isDemo = location.pathname.includes('/demo');
                  // グループ画面は完全一致でチェック（/ai-ocrが/groups/.../ai-ocrに誤マッチしないように）
                  const isGroupLink =
                    button.link.startsWith('/groups/') && button.link.includes('/ai-ocr');
                  const isSelected = isDemo
                    ? button.link.includes('/demo')
                    : isGroupLink
                      ? location.pathname.startsWith(button.link)
                      : button.link === '/ai-ocr'
                        ? location.pathname === '/ai-ocr' ||
                          location.pathname.startsWith('/ai-ocr/')
                        : location.pathname.includes(button.link);

                  return (
                    <ListItem sx={styles.listItem} key={button.text}>
                      <ListItemButton
                        sx={{
                          ...styles.button,
                          ...(isSelected ? styles.buttonSelected : {}),
                        }}
                        key={button.text}
                        onClick={(): void => {
                          navigate(button.link);
                          setMenuOpen(false);
                        }}
                      >
                        <ListItemIcon sx={styles.itemIcon}>{button.icon}</ListItemIcon>
                        <ListItemText
                          primary={button.text}
                          sx={{ ...styles.buttonText, textAlign: 'left' }}
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            </Box>
            <Box sx={styles.bottomButtons}>
              {bottomButtonList.map(button => (
                <ListItem sx={styles.listItem} key={button.text}>
                  <ListItemButton
                    sx={{
                      ...styles.button,
                    }}
                    onClick={logoutButton}
                  >
                    <ListItemIcon sx={styles.itemIcon}>
                      <LogoutIcon sx={styles.icon} />
                    </ListItemIcon>
                    <ListItemText primary="ログアウト" sx={styles.buttonText} />
                  </ListItemButton>
                </ListItem>
              ))}
            </Box>
            <Divider sx={{ bgcolor: '#fff', opacity: '50%' }} />
            <Box sx={styles.account}>
              <Box alignItems="center" sx={styles.companyInfo}>
                <Avatar src={user?.icon ?? AccountCircle} sx={styles.avatar} />
                <Typography sx={styles.companyName}>{user?.name}</Typography>
              </Box>
            </Box>
          </Box>
        </>
      )}
      <CustomSnackbar text={errorText} autoHideDuration={6000} open={open} />
    </>
  );
});

SideBar.displayName = 'SideBar';
export default SideBar;
